import { useEffect, useState, useCallback, useRef } from "react";
import UserAgent from "../Utils/UserAgent";
import TriggerActionService from "../TriggerActionService";
import ActionTriggerConstants from "../ActionTriggerConstants";
import Constants from "../Constants";
import cookieManager from "../CookieManager";

export default function usePWAInstall() {
    const deferredRef = useRef(null);

    const [canInstall, setCanInstall] = useState(false);
    const [isStandalone, setIsStandalone] = useState(UserAgent.isStandalone());

    const countryCode = cookieManager.get(Constants.COUNTRY_CODE);
    const isIOS = UserAgent.isIOS();
    const isMobile = UserAgent.isMobile();
    const isTablet = UserAgent.isTablet();
    const isAndroid = UserAgent.isAndroid();
    const isDesktop = UserAgent.isDesktop();

    // === Capture BIP from pre-react (optional queue) ===
    useEffect(() => {
        if (!deferredRef.current && Array.isArray(window.__bipQueue) && window.__bipQueue.length > 0) {
            const evt = window.__bipQueue.shift();
            if (evt) {
                try { evt.preventDefault?.(); } catch(_) {}
                deferredRef.current = evt;
                setCanInstall(true);
                console.log("[PWA] beforeinstallprompt captured from pre-react queue");
            }
        }
    }, []);

    // === PWA install listeners ===
    useEffect(() => {
        if (!(isMobile || isTablet) || isStandalone) {
            console.log("[PWA] install listeners skipped — isMobile:", isMobile, "| isTablet:", isTablet, "| isStandalone:", isStandalone);
            return;
        }

        const onBeforeInstallPrompt = (e) => {
            e.preventDefault();
            deferredRef.current = e;
            setCanInstall(true);
            console.log("[PWA] beforeinstallprompt captured");
        };

        const onAppInstalled = () => {
            deferredRef.current = null;
            setCanInstall(false);
            setIsStandalone(true);

            console.log("[PWA] App installed");

            TriggerActionService.sendTriggerAction(
                ActionTriggerConstants.PWA_INSTALLED,
                "",
                {}
            ).catch((e) => console.warn("[-] TriggerActionService failed", e));
        };

        window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
        window.addEventListener("appinstalled", onAppInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
            window.removeEventListener("appinstalled", onAppInstalled);
        };
    }, [isMobile, isTablet, isStandalone]);

    // === Re-probe BIP on visibility ===
    const probeForInstallEvent = useCallback((timeoutMs = 2500) => {
        return new Promise((resolve) => {
            if (deferredRef.current) {
                setCanInstall(true);
                return resolve(true);
            }

            let timeoutId;
            const onTempBIP = (e) => {
                e.preventDefault();
                clearTimeout(timeoutId);
                cleanup();
                deferredRef.current = e;
                setCanInstall(true);
                console.log("[PWA] beforeinstallprompt re-captured on resume");
                resolve(true);
            };

            const onVis = () => {
                if (document.visibilityState !== "visible") {
                    clearTimeout(timeoutId);
                    cleanup();
                    resolve(false);
                }
            };

            const cleanup = () => {
                window.removeEventListener("beforeinstallprompt", onTempBIP);
                document.removeEventListener("visibilitychange", onVis);
            };

            window.addEventListener("beforeinstallprompt", onTempBIP, { once: true });
            document.addEventListener("visibilitychange", onVis);

            timeoutId = setTimeout(() => {
                cleanup();
                setCanInstall(!!deferredRef.current);
                resolve(false);
            }, timeoutMs);
        });
    }, []);

    useEffect(() => {
        const onVisibilityChange = () => {
            if (document.visibilityState !== "visible") return;

            if (UserAgent.isStandalone()) {
                deferredRef.current = null;
                setCanInstall(false);
                setIsStandalone(true);
            } else if (!deferredRef.current) {
                setCanInstall(false);
                probeForInstallEvent();
            }
        };

        document.addEventListener("visibilitychange", onVisibilityChange);
        return () => document.removeEventListener("visibilitychange", onVisibilityChange);
    }, [probeForInstallEvent]);

    useEffect(() => {
        if ("serviceWorker" in navigator) {
            navigator.serviceWorker.ready
                .then(() => probeForInstallEvent(0))
                .catch(() => {});
        }
    }, [probeForInstallEvent]);

    const promptInstall = useCallback(async () => {
        const evt = deferredRef.current;
        if (!evt) return { outcome: "unavailable" };
        try {
            await evt.prompt();
            const choice = await evt.userChoice;
            deferredRef.current = null;
            setCanInstall(false);
            return { outcome: choice?.outcome || "unknown" };
        } catch (error) {
            console.warn("[PWA] prompt() failed, clearing saved event:", error);
            deferredRef.current = null;
            setCanInstall(false);
            return { outcome: "error", error };
        }
    }, []);

    // === Unified manual A2HS support (iOS + Android) ===
    const hasBIP = typeof window !== "undefined" && "onbeforeinstallprompt" in window;

    const isAndroidManualBrowser = (() => {
        if (!isAndroid) return false;
        return UserAgent.isMobilePWAManualInstallable();
    })();

    const isDesktopManualBrowser = (() => {
        if (!isDesktop) return false;
        return UserAgent.isFirefox() || UserAgent.isSafari();
    })();

    const supportsManualAddToHome =
        !isStandalone &&
        !UserAgent.isEmbeddedWebView() &&
        (
            isIOS ||
            (isAndroid && !hasBIP && isAndroidManualBrowser) ||
            (isDesktop && !hasBIP && isDesktopManualBrowser)
        );

    return {
        isStandalone,
        countryCode,
        canInstall,
        promptInstall,
        supportsManualAddToHome,
    };
}
